import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { ExpandableCard, ExpandableCardHandle, CardSection, CardDropdownMenu } from '@/components/Cards';
import { ChangeRequest, BatchedChanges, SellerChangeRequestStatus, ChangeRequestWithChatInfo } from '@/types';
import ScrollToWrapper from '@/components/ScrollToWrapper';
import ChangeRequestDetails, { isChangeRequestLocked } from '../ChangeRequestDetails';
import { RejectedStatusReason, ClosedStatusReason } from '@/components/StatusReason';
import { ChangeRequestSellerStatusIndicator } from '@/components/StatusIndicator';
import { ResidentialUnitTooltip } from '@/seller/components/data-tooltips';
import { Tooltip } from '@/components/Tooltip';
import Tags from '@/components/Tags';
import ProfileAndUserDisplay from '@/components/ProfileAndUserDisplay';
import { faArchive, faClone } from '@fortawesome/pro-light-svg-icons';
import { archiveChangeRequest } from '@/services/change-requests';
import ChangeRequestSplitDialog from '../ChangeRequestSplitDialog';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { YesNoDialog } from '@/components/dialogs';
import Banner from '@/components/Banner';
import { ProjectPermissionToggle } from '@/components/permissions';
import { Dropdown } from '@ygtechnologies/rubus-ui';

export interface ChangeRequestListItemProps {
    changeRequest: ChangeRequestWithChatInfo;
    reloadChangeRequests(): void;
    onChange(changes: BatchedChanges<ChangeRequest>): void;
    defaultExpanded?: boolean;
    showExpanded?: boolean;
}

const ChangeRequestListItem: React.FC<ChangeRequestListItemProps> = ({
    changeRequest,
    reloadChangeRequests,
    onChange,
    defaultExpanded,
    showExpanded = true,
}) => {
    const [expanded, setExpanded] = useState(!!defaultExpanded);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showSplitDialog, setShowSplitDialog] = useState(false);
    const [showChangeRequestArchiveDialog, setShowExternalAgreementArchiveDialog] = useState(false);
    const onLoad = () => {
        setIsLoaded(true);
    };

    const toggleIsArchived = async () => {
        await archiveChangeRequest(changeRequest.id, !changeRequest.isArchived);
        reloadChangeRequests();

        // It's easy to change the status to archived.
        // From archived back to the correct status is not easy. And right now we only do this calculation in the backend.
        // onChange(
        //     BatchedChanges.single(changeRequest.id, {
        //         isArchived: !changeRequest.isArchived,
        //         status: SellerChangeRequestStatus.Archived,
        //     })
        // );
    };

    /**
     * This is also unArchive click
     */
    const handleArchiveClick = () => {
        setShowExternalAgreementArchiveDialog(true);
    };

    const isLocked = isChangeRequestLocked(changeRequest);

    return (
        <>
            <ScrollToWrapper shouldScroll={defaultExpanded && isLoaded}>
                <ExpandableCard
                    notificationBadgeNumber={changeRequest.unreadMessages}
                    type="inside-bordered"
                    expanded={expanded}
                    expandableContent={
                        <ChangeRequestDetails onLoad={onLoad} changeRequest={changeRequest} onChange={onChange} />
                    }>
                    <CardSection caption={<Trans>Request ID</Trans>} width="80px">
                        #{changeRequest.id}
                    </CardSection>

                    <CardSection width="120px" caption={<Trans>Unit</Trans>}>
                        <ResidentialUnitTooltip residentialUnitId={changeRequest.residentialUnitId} linkTab="changes">
                            <span>{changeRequest.residentialUnitName}</span>
                        </ResidentialUnitTooltip>
                    </CardSection>

                    <CardSection minWidth="100px" caption={<Trans>Request</Trans>}>
                        <Tooltip
                            includeQuestionMark
                            color="light"
                            title={changeRequest.subject}
                            content={<div className="preserve-white-space">{changeRequest.description}</div>}>
                            <div className="text-truncate">{changeRequest.subject}</div>
                        </Tooltip>
                    </CardSection>

                    {changeRequest.createdByUser && (
                        <CardSection right width="200px">
                            <ProfileAndUserDisplay
                                mode="user"
                                userId={changeRequest.createdByUser.id}
                                userName={changeRequest.createdByUser.name}
                                prependContent={
                                    <small>
                                        <Trans>Created by</Trans>
                                    </small>
                                }
                            />
                        </CardSection>
                    )}

                    <CardSection caption={<Trans>Tags</Trans>} right width="250px">
                        {changeRequest.tags.length > 0 && <Tags tags={changeRequest.tags} size="small" limit={1} />}

                        {changeRequest.tags.length === 0 && (
                            <span className="text-missing">
                                <Trans>No tags</Trans>
                            </span>
                        )}
                    </CardSection>

                    <CardSection caption={<Trans>Internal status</Trans>} width="160px" right>
                        <ChangeRequestSellerStatusIndicator value={changeRequest.status} size="small" />

                        {changeRequest.status === SellerChangeRequestStatus.Rejected && (
                            <RejectedStatusReason
                                reason={changeRequest.rejectReason}
                                userName={changeRequest.reviewedByUser?.name}
                            />
                        )}

                        {changeRequest.status === SellerChangeRequestStatus.Closed && (
                            <ClosedStatusReason
                                reason={changeRequest.closeNote}
                                userName={changeRequest.closedByUser?.name}
                            />
                        )}
                    </CardSection>
                    {showExpanded && <ExpandableCardHandle onExpandToggle={setExpanded} expanded={expanded} />}
                    {/* <CardTags tags={changeRequest.tags} /> */}

                    <CardDropdownMenu>
                        <Dropdown.Item icon={faArchive} disabled={isLocked} onClick={handleArchiveClick}>
                            {changeRequest.status === SellerChangeRequestStatus.Archived ? (
                                <Trans>Unarchive change request</Trans>
                            ) : (
                                <Trans>Archive change request</Trans>
                            )}
                        </Dropdown.Item>

                        <ProjectPermissionToggle condition="canSendChangeRequests">
                            <Dropdown.Item
                                icon={faClone}
                                onClick={() => {
                                    setShowSplitDialog(true);
                                }}
                                disabled={isLocked}>
                                <Trans>Split request</Trans>
                            </Dropdown.Item>
                        </ProjectPermissionToggle>
                    </CardDropdownMenu>
                </ExpandableCard>
            </ScrollToWrapper>
            <YesNoDialog
                onClose={() => setShowExternalAgreementArchiveDialog(false)}
                onAccept={() => toggleIsArchived()}
                show={showChangeRequestArchiveDialog}
                title={
                    changeRequest.isArchived ? (
                        <Trans>Unarchive change request</Trans>
                    ) : (
                        <Trans>Archive change request</Trans>
                    )
                }
                body={
                    changeRequest.isArchived ? (
                        <>
                            <p>
                                <Trans>Are you sure you want to unarchive this change request?</Trans>
                            </p>
                            {changeRequest.externalAgreementId && (
                                <Banner type="warning">
                                    <Trans>
                                        <strong>Please note</strong> this will also set the corresponding external agreement
                                        to approved.
                                    </Trans>
                                </Banner>
                            )}
                        </>
                    ) : (
                        <>
                            <p>
                                <Trans>Are you sure you want to archive this change request?</Trans>
                            </p>
                            {changeRequest.externalAgreementId && (
                                <Banner type="warning">
                                    <Trans>
                                        <strong>Please note</strong> this will also cancel the corresponding external
                                        agreement.
                                    </Trans>
                                </Banner>
                            )}
                        </>
                    )
                }
            />
            <ChangeRequestSplitDialog
                changeRequestId={changeRequest.id}
                show={showSplitDialog}
                onClose={() => setShowSplitDialog(false)}
                onSave={() => triggerHandler(reloadChangeRequests)}
            />
        </>
    );
};

export default ChangeRequestListItem;
